import { IkeUnit } from '@ikegps/core-measurement';
import type { ExportNotificationEntry, ValidationMessage } from '@ikegps/core-api';
import {
	type CollectionModel,
	type CollectionModelField,
	CollectionModelFieldHeight,
	CollectionModelFieldSubForm,
	describeFieldPath,
} from '@ikegps/core-shared';
import { T, Translate } from '../../../i18n';
import { Services } from '../../../services';

export const MessageReasonTranslations: { [code in ValidationMessage['reason']]: T } = {
	agl: T.NOTIFICATION_REASON_AGL,
	nolocation: T.NOTIFICATION_REASON_NO_LOCATION,
	noguywire: T.NOTIFICATION_REASON_NO_GUY_WIRE,
	clientitem: T.NOTIFICATION_REASON_CLIENT_ITEM,
	other: T.NOTIFICATION_REASON_OTHER,
	reason_only: T.NOTIFICATION_REASON_ONLY,
	missing: T.NOTIFICATION_REASON_MISSING,
	shapefile_value_truncated: T.REASON_SHAPEFILE_VALUE_TRUNCATED,
	shapefile_line_style_conflict: T.REASON_SHAPEFILE_LINE_STYLE_CONFLICT,

	pla_count_on_span_mismatch: T.REASON_PLA_COUNT_ON_SPAN_MISMATCH,
	pla_collection_link_one_way: T.REASON_PLA_COLLECTION_LINK_ONE_WAY,
	pla_span_same_type_linked: T.REASON_PLA_SPAN_SAME_TYPE_LINKED,
	ipf_span_other_to_other_linked: T.REASON_IPF_SPAN_OTHER_TO_OTHER_LINKED,
	pla_multiple_spans_linked: T.REASON_PLA_MULTIPLE_SPANS_LINKED,
	pla_power_circuit_phase_height_mismatch: T.REASON_PLA_POWER_CIRCUIT_PHASE_HEIGHT_MISMATCH,
	pla_power_circuit_group_mismatch: T.REASON_PLA_POWER_CIRCUIT_GROUP_MISMATCH,
	pla_circuit_type_mismatch: T.REASON_PLA_CIRCUIT_TYPE_MISMATCH,

	ipf_anchor_assembly_type_not_found: T.REASON_IPF_ANCHOR_ASSEMBLY_TYPE_NOT_FOUND,
	ipf_anchor_lead_length_missing: T.REASON_IPF_ANCHOR_LEAD_LENGTH_MISSING,
	ipf_anchor_too_many_guys: T.REASON_IPF_ANCHOR_TOO_MANY_GUYS,
	ipf_conductor_type_not_found: T.REASON_IPF_CONDUCTOR_TYPE_NOT_FOUND,
	ipf_cycle_detected: T.REASON_IPF_CYCLE_DETECTED,
	ipf_isolated_poles_detected: T.REASON_IPF_ISOLATED_POLES_DETECTED,
	ipf_equipment_attachment_height_missing: T.REASON_IPF_EQUIPMENT_ATTACHMENT_HEIGHT_MISSING,
	ipf_equipment_orientation_missing: T.REASON_IPF_EQUIPMENT_ORIENTATION_MISSING,
	ipf_equipment_type_not_found: T.REASON_IPF_EQUIPMENT_TYPE_NOT_FOUND,
	ipf_framing_type_not_found: T.REASON_IPF_FRAMING_TYPE_NOT_FOUND,
	ipf_guy_attachment_height_missing: T.REASON_IPF_GUY_ATTACHMENT_HEIGHT_MISSING,
	ipf_guy_type_not_found: T.REASON_IPF_GUY_TYPE_NOT_FOUND,
	ipf_guy_insulator_type_not_found: T.REASON_IPF_GUY_INSULATOR_TYPE_NOT_FOUND,
	ipf_joint_use_cable_type_not_found: T.REASON_IPF_JOINT_USE_CABLE_TYPE_NOT_FOUND,
	ipf_multiple_spans_same_type: T.REASON_IPF_MULTIPLE_SPANS_SAME_TYPE,
	ipf_power_circuit_type_not_found: T.REASON_IPF_POWER_CIRCUIT_TYPE_NOT_FOUND,
	ipf_service_cannot_be_collection_linked: T.REASON_IPF_SERVICE_CANNOT_BE_COLLECTION_LINKED,
	ipf_service_length_missing: T.REASON_IPF_SERVICE_LENGTH_MISSING,
	ipf_service_endpoint_missing: T.REASON_IPF_SERVICE_ENDPOINT_MISSING,
	ipf_target_collection_not_found: T.REASON_IPF_TARGET_COLLECTION_NOT_FOUND,
	ipf_service_multiple_power_circuits: T.REASON_IPF_SERVICE_MULTIPLE_POWER_CIRCUITS,
	ipf_service_multiple_telephone_cables: T.REASON_IPF_SERVICE_MULTIPLE_TELEPHONE_CABLES,
	ipf_service_multiple_catv_cables: T.REASON_IPF_SERVICE_MULTIPLE_CATV_CABLES,
	ipf_required_field: T.REASON_IPF_REQUIRED_FIELD,
	ipf_power_circuit_needs_group: T.REASON_IPF_POWER_CIRCUIT_NEEDS_GROUP,
	ipf_power_circuit_primary_phase_height_missing: T.REASON_IPF_POWER_CIRCUIT_PRIMARY_PHASE_HEIGHT_MISSING,
	ipf_power_circuit_secondary_phase_height_missing: T.REASON_IPF_POWER_CIRCUIT_SECONDARY_PHASE_HEIGHT_MISSING,
	ipf_location_fallback_to_photo: T.REASON_IPF_LOCATION_FALLBACK_TO_PHOTO,
	ipf_altitude_zero: T.REASON_IPF_ALTITUDE_ZERO,
	ipf_secondary_multiplex_more_than_1_attachment_height: T.REASON_IPF_SECONDARY_MULTIPLEX_MORE_THAN_1_ATTACHMENT_HEIGHT,

	dds_required_field: T.REASON_DDS_REQUIRED_FIELD,
	dds_recommended_field: T.REASON_DDS_RECOMMENDED_FIELD,
	dds_location_fallback_to_photo: T.REASON_DDS_LOCATION_FALLBACK_TO_PHOTO,
	dds_equipment_type_not_found: T.REASON_DDS_EQUIPMENT_TYPE_NOT_FOUND,
	dds_negative_circumference: T.REASON_DDS_NEGATIVE_CIRCUMFERENCE,
	dds_temperature_without_midspan: T.REASON_DDS_TEMPERATURE_WITHOUT_MIDSPAN,
	dds_temperature_negative: T.REASON_DDS_TEMPERATURE_NEGATIVE,

	spida_calc_title_invalid: T.REASON_SPIDA_CALC_TITLE_INVALID,

	// spida calc span linking
	spida_calc_span_linking_target_pole_not_found: T.REASON_SPIDA_CALC_SPAN_LINKING_TARGET_POLE_NOT_FOUND,
	spida_calc_span_linking_span_mismatch: T.REASON_SPIDA_CALC_SPAN_LINKING_SPAN_MISMATCH,
	spida_calc_span_linking_wires_count_mismatch: T.REASON_SPIDA_CALC_SPAN_LINKING_WIRES_COUNT_MISMATCH,
	spida_calc_span_linking_wires_too_many: T.REASON_SPIDA_CALC_SPAN_LINKING_WIRES_TOO_MANY,
	spida_calc_span_linking_span_guys_count_mismatch: T.REASON_SPIDA_CALC_SPAN_LINKING_SPAN_GUYS_COUNT_MISMATCH,
	spida_calc_span_linking_wire_owner_mismatch: T.REASON_SPIDA_CALC_SPAN_LINKING_WIRE_OWNER_MISMATCH,
	spida_calc_span_linking_wire_usage_group_mismatch: T.REASON_SPIDA_CALC_SPAN_LINKING_WIRE_USAGE_GROUP_MISMATCH,
	spida_calc_span_linking_wire_size_mismatch: T.REASON_SPIDA_CALC_SPAN_LINKING_WIRE_SIZE_MISMATCH,
	spida_calc_span_linking_wire_tension_group_mismatch: T.REASON_SPIDA_CALC_SPAN_LINKING_WIRE_TENSION_GROUP_MISMATCH,
	spida_calc_span_linking_span_guy_owner_mismatch: T.REASON_SPIDA_CALC_SPAN_LINKING_SPAN_GUY_OWNER_MISMATCH,
	spida_calc_span_linking_span_guy_usage_group_mismatch: T.REASON_SPIDA_CALC_SPAN_LINKING_SPAN_GUY_USAGE_GROUP_MISMATCH,
	spida_calc_span_linking_span_guy_size_mismatch: T.REASON_SPIDA_CALC_SPAN_LINKING_SPAN_GUY_SIZE_MISMATCH,
	spida_calc_span_linking_span_guy_tension_group_mismatch:
		T.REASON_SPIDA_CALC_SPAN_LINKING_SPAN_GUY_TENSION_GROUP_MISMATCH,
	spida_calc_span_linking_span_guy_attachment_height_exceed_agl:
		T.REASON_SPIDA_CALC_SPAN_LINKING_SPAN_GUY_ATTACHMENT_HEIGHT_EXCEED_AGL,
	spida_calc_span_linking_span_guy_mid_span_height_exceed_agl:
		T.REASON_SPIDA_CALC_SPAN_LINKING_SPAN_GUY_MID_SPAN_HEIGHT_EXCEED_AGL,
	spida_calc_span_linking_span_guy_height_exceed_agl: T.REASON_SPIDA_CALC_SPAN_LINKING_SPAN_GUY_HEIGHT_EXCEED_AGL,
};

/**
 * Number field Ids which are expressed in imperial units only.
 *
 *  - GLC (")
 *  - Circumference (")
 */
const IMPERIAL_NUMBER_FIELDS = ['circumference'];

export class ExportNotificationDetailCtrl {
	private notificationEntry: ExportNotificationEntry;
	readonly customizedFieldTitle?: string;
	private collection: CollectionModel;
	private field: CollectionModelField | null;

	constructor(notificationEntry: ExportNotificationEntry, collection: CollectionModel) {
		this.notificationEntry = notificationEntry;
		this.customizedFieldTitle = notificationEntry.reason?.customizedFieldTitle;
		this.collection = collection;
		this.field = this.getCollectionField();
	}

	getFieldName(): string {
		if (this.customizedFieldTitle) {
			return this.customizedFieldTitle;
		}
		if (this.notificationEntry.field == null) {
			return '';
		}
		if (this.field != null) {
			return this.field.schema.displayName;
		}
		// Fall back to the offending value preview when the producer couldn't
		// supply a resolvable fieldId — better than rendering "Unknown" for the
		// user. Matches the doc comment on `ExportNotificationEntry.field.value`.
		const fallback = this.notificationEntry.field.value;
		return fallback != null && fallback !== '' ? fallback : Translate(T.UNKNOWN);
	}

	getFieldPath(): string[] {
		if (this.notificationEntry.field == null) {
			return [];
		}
		if (this.field == null) {
			// No parent context when resolution fails; suppress the path rather
			// than rendering a misleading "Unknown / Unknown".
			return [];
		}
		return describeFieldPath(this.field);
	}

	getMessageCode(): string {
		return this.notificationEntry.code;
	}

	getReason(): string | null {
		if (this.notificationEntry.reason == null) {
			return null;
		}
		const reason = this.notificationEntry.reason.reason;
		const translationKey = MessageReasonTranslations[reason];
		if (translationKey == null) {
			return null;
		}
		return Translate(translationKey, this.notificationEntry.reason);
	}

	hasDefaultValue(): boolean {
		return this.notificationEntry.defaultValue != null;
	}

	getDefaultValue(): string {
		const defaultValue = this.notificationEntry.defaultValue || Translate(T.UNKNOWN);

		if (this.field == null) {
			return defaultValue;
		}

		if (CollectionModelFieldHeight.isHeightField(this.field)) {
			const targetUnit = Services.UserSettings.units;
			return IkeUnit.display(parseFloat(defaultValue), targetUnit, IkeUnit.METER);
		}

		if (IMPERIAL_NUMBER_FIELDS.indexOf(this.field.schema.id) !== -1) {
			return IkeUnit.display(parseFloat(defaultValue), IkeUnit.INCH, IkeUnit.METER);
		}

		return defaultValue;
	}

	private getCollectionField(): CollectionModelField | null {
		const notificationField = this.notificationEntry.field;
		if (notificationField == null) {
			return null;
		}
		if (notificationField.formId == null) {
			if (this.collection.hasField(notificationField.fieldId)) {
				return this.collection.getField(notificationField.fieldId);
			} else {
				return null;
			}
		}

		let targetField: CollectionModelField | null = null;
		this.collection.forEachField((field) => {
			if (CollectionModelFieldSubForm.isSubFormField(field)) {
				const instance = field.getInstance(notificationField.formId);
				if (instance != null && instance.hasField(notificationField.fieldId)) {
					targetField = instance.getField(notificationField.fieldId);
					return false;
				} else if (field.schema.id === notificationField.fieldId) {
					targetField = field;
					return false;
				}
			}

			return true;
		});

		return targetField;
	}
}
